import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import BusinessIcon from '@material-ui/icons/Business'
import HomeWorkIcon from '@material-ui/icons/HomeWork'

const CONNECTION_TYPE_OPTIONS = [
  { value: 'hose_bib', label: 'Hose Bib' },
  { value: 'hydrant', label: 'Fire Hydrant' },
  { value: 'overhead_dump', label: 'Overhead Dump' },
  { value: 'pond_lake', label: 'Pond / Lake' },
  { value: 'tank_cistern', label: 'Tank / Cistern' },
  { value: 'spigot', label: 'Spigot' },
  { value: 'quick_coupler', label: 'Quick Coupler' },
  { value: 'other', label: 'Other' }
]

const CONNECTION_SIZE_OPTIONS = [
  { value: '0.5', label: '1/2"' },
  { value: '0.75', label: '3/4"' },
  { value: '1.0', label: '1"' },
  { value: '1.5', label: '1 1/2"' },
  { value: '2.0', label: '2"' }
]

const COST_TYPE_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
  { value: 'permit_required', label: 'Permit Required' },
  { value: 'unknown', label: 'Unknown' }
]

const AVAILABILITY_SCOPE_OPTIONS = [
  { value: 'general', label: 'Available Anytime' },
  { value: 'job_only', label: 'Just for Their Job' },
  { value: 'permission_required', label: 'Permission Required' }
]

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontWeight: 600,
    color: '#20342b',
    fontSize: '0.85rem',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  addressWrapper: {
    position: 'relative'
  },
  suggestionsDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1300,
    maxHeight: 220,
    overflowY: 'auto',
    border: '1px solid #ddd',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px'
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#333',
    '&:hover': {
      backgroundColor: '#e8f0ea'
    }
  },
  suggestionIcon: {
    color: '#5d6c64',
    fontSize: 18,
    flexShrink: 0
  },
  coordsResolved: {
    fontSize: '0.75rem',
    color: '#2e7d32',
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  }
}))

const EMPTY_FORM = {
  name: '',
  connectionType: '',
  connectionSize: '',
  latitude: '',
  longitude: '',
  addressInput: '',
  addressStreet: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  contactNotes: '',
  costType: 'unknown',
  costNotes: '',
  notes: '',
  availabilityScope: 'job_only',
  estimatedFillMinutes: ''
}

function formFromItem (item) {
  if (!item) return { ...EMPTY_FORM }
  const coords = item.location?.coordinates || []
  const parts = [item.address?.street, item.address?.city, item.address?.state, item.address?.zip]
    .filter(Boolean)
  return {
    name: item.name || '',
    connectionType: item.connectionType || '',
    connectionSize: item.connectionSize || '',
    longitude: coords[0] != null ? String(coords[0]) : '',
    latitude: coords[1] != null ? String(coords[1]) : '',
    addressInput: parts.join(', '),
    addressStreet: item.address?.street || '',
    addressCity: item.address?.city || '',
    addressState: item.address?.state || '',
    addressZip: item.address?.zip || '',
    contactName: item.contactName || '',
    contactPhone: item.contactPhone || '',
    contactEmail: item.contactEmail || '',
    contactNotes: item.contactNotes || '',
    costType: item.costType || 'unknown',
    costNotes: item.costNotes || '',
    notes: item.notes || '',
    availabilityScope: item.availabilityScope || 'job_only',
    estimatedFillMinutes: item.estimatedFillMinutes != null ? String(item.estimatedFillMinutes) : ''
  }
}

function formToPayload (form) {
  return {
    name: form.name.trim(),
    connectionType: form.connectionType,
    connectionSize: form.connectionSize,
    location: {
      type: 'Point',
      coordinates: [parseFloat(form.longitude), parseFloat(form.latitude)]
    },
    address: {
      street: form.addressStreet.trim(),
      city: form.addressCity.trim(),
      state: form.addressState.trim(),
      zip: form.addressZip.trim(),
      formatted: [form.addressStreet, form.addressCity, form.addressState, form.addressZip]
        .map(s => s.trim()).filter(Boolean).join(', ')
    },
    contactName: form.contactName.trim(),
    contactPhone: form.contactPhone.trim(),
    contactEmail: form.contactEmail.trim(),
    contactNotes: form.contactNotes.trim(),
    costType: form.costType,
    costNotes: form.costNotes.trim(),
    notes: form.notes.trim(),
    availabilityScope: form.availabilityScope,
    estimatedFillMinutes: form.estimatedFillMinutes ? parseInt(form.estimatedFillMinutes) : null
  }
}

const WaterAccessFormDialog = ({ open, onClose, onSave, editItem, apiBase }) => {
  const classes = useStyles()
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Name lookup state
  const [nameResults, setNameResults] = useState([])
  const [showNameResults, setShowNameResults] = useState(false)
  const [nameSearching, setNameSearching] = useState(false)
  const nameTimer = useRef(null)

  // Address typeahead state
  const [suggestions, setSuggestions] = useState([])
  const [searching, setSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [addressResolved, setAddressResolved] = useState(false)
  const searchTimer = useRef(null)

  useEffect(() => {
    if (open) {
      setForm(formFromItem(editItem))
      setError('')
      setSaving(false)
      setSuggestions([])
      setShowSuggestions(false)
      setAddressResolved(!!editItem)
      setSearching(false)
      setNameResults([])
      setShowNameResults(false)
    }
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
      if (nameTimer.current) clearTimeout(nameTimer.current)
    }
  }, [open, editItem])

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  // Name lookup — search Vendors and JobSites as user types Location Name
  const handleNameInput = (e) => {
    const val = e.target.value
    setForm(prev => ({ ...prev, name: val }))

    if (nameTimer.current) clearTimeout(nameTimer.current)

    if (val.trim().length < 2) {
      setNameResults([])
      setShowNameResults(false)
      return
    }

    nameTimer.current = setTimeout(async () => {
      setNameSearching(true)
      try {
        const base = apiBase || '/api/water-access'
        const resp = await axios.get(`${base}/name-lookup`, { params: { q: val.trim() } })
        const results = resp.data?.results || []
        setNameResults(results)
        setShowNameResults(results.length > 0)
      } catch (err) {
        console.error('Name lookup error:', err)
      } finally {
        setNameSearching(false)
      }
    }, 300)
  }

  const handleSelectNameResult = async (result) => {
    const addressStr = [result.address, result.city, result.state, result.zip].filter(Boolean).join(', ')
    setForm(prev => ({
      ...prev,
      name: result.name || prev.name,
      addressStreet: result.address || prev.addressStreet,
      addressCity: result.city || prev.addressCity,
      addressState: result.state || prev.addressState,
      addressZip: result.zip || prev.addressZip,
      addressInput: addressStr,
      latitude: result.lat != null ? String(result.lat) : prev.latitude,
      longitude: result.lng != null ? String(result.lng) : prev.longitude
    }))
    setNameResults([])
    setShowNameResults(false)

    if (result.lat != null) {
      setAddressResolved(true)
    } else if (result.address) {
      // No coordinates (e.g. vendor) — auto-geocode using just the street
      // Send "3425 S 43rd Ave" not the full "3425 S. 43d Ave, Phoenix, AZ, 85009"
      // so the server uses structured search (street= param) correctly
      const cleanStreet = (result.address || '')
        .replace(/\./g, '')  // remove periods ("S." → "S")
        .replace(/\b(\d+)(d|st|nd|rd|th)\b/gi, (_, num, suf) => {  // fix ordinals ("43d" → "43rd")
          const n = parseInt(num)
          const s = n % 100
          if (s >= 11 && s <= 13) return num + 'th'
          const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' }
          return num + (suffixes[n % 10] || 'th')
        })
      try {
        const base = apiBase || '/api/water-access'
        const resp = await axios.get(`${base}/address-search`, { params: { q: cleanStreet } })
        const results = resp.data?.suggestions || []
        if (results.length > 0 && results[0].lat) {
          setForm(prev => ({
            ...prev,
            latitude: String(results[0].lat),
            longitude: String(results[0].lng)
          }))
          setAddressResolved(true)
        }
      } catch (err) {
        console.error('Auto-geocode after name select error:', err)
      }
    }
  }

  const handleAddressInput = (e) => {
    const val = e.target.value
    setForm(prev => ({ ...prev, addressInput: val }))
    setAddressResolved(false)

    if (searchTimer.current) clearTimeout(searchTimer.current)

    if (val.trim().length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    // Filter cached suggestions to only show ones relevant to current input
    if (suggestions.length > 0) {
      const words = val.trim().toLowerCase().split(/\s+/)
      const filtered = suggestions.filter(s => {
        const formatted = s.formatted?.toLowerCase() || ''
        // Every word the user typed should appear somewhere in the suggestion
        return words.every(w => formatted.includes(w))
      })
      if (filtered.length === 0) {
        setShowSuggestions(false)
      }
    }

    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const base = apiBase || '/api/water-access'
        const resp = await axios.get(`${base}/address-search`, { params: { q: val.trim() } })
        const results = resp.data?.suggestions || []
        if (results.length > 0) {
          setSuggestions(results)
          setShowSuggestions(true)
        }
        // If 0 results, keep previous suggestions visible (but already filtered above)
      } catch (err) {
        console.error('Address search error:', err)
      } finally {
        setSearching(false)
      }
    }, 400)
  }

  const handleSelectSuggestion = (suggestion) => {
    setForm(prev => ({
      ...prev,
      addressInput: suggestion.formatted,
      addressStreet: suggestion.street || '',
      addressCity: suggestion.city || '',
      addressState: suggestion.state || '',
      addressZip: suggestion.zip || '',
      latitude: String(suggestion.lat),
      longitude: String(suggestion.lng)
    }))
    setSuggestions([])
    setShowSuggestions(false)
    setAddressResolved(true)
  }

  const isValid = form.name.trim() && form.connectionType && form.latitude && form.longitude

  const handleSubmit = async () => {
    if (!isValid) return
    const lat = parseFloat(form.latitude)
    const lon = parseFloat(form.longitude)
    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      setError('Invalid coordinates. Latitude: -90 to 90, Longitude: -180 to 180.')
      return
    }

    setSaving(true)
    setError('')
    try {
      await onSave(formToPayload(form), editItem?._id)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 700, color: '#20342b' }}>
        {editItem ? 'Edit Water Access Point' : 'Add Water Access Point'}
      </DialogTitle>
      <DialogContent style={{ overflow: 'visible' }}>
        {error && (
          <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
            {error}
          </Typography>
        )}

        <ClickAwayListener onClickAway={() => setShowNameResults(false)}>
          <div className={classes.addressWrapper}>
            <TextField
              label="Location Name"
              value={form.name}
              onChange={handleNameInput}
              fullWidth
              required
              margin="dense"
              size="small"
              placeholder="e.g. AZ Water Depot — or search vendors/jobsites"
              autoComplete="off"
              InputProps={{
                endAdornment: nameSearching ? <CircularProgress size={16} style={{ marginRight: 4 }} /> : null
              }}
            />
            {showNameResults && nameResults.length > 0 && (
              <Paper className={classes.suggestionsDropdown} elevation={4}>
                {nameResults.map((r, idx) => (
                  <div
                    key={idx}
                    className={classes.suggestionItem}
                    onClick={() => handleSelectNameResult(r)}
                  >
                    {r.type === 'vendor'
                      ? <BusinessIcon className={classes.suggestionIcon} />
                      : <HomeWorkIcon className={classes.suggestionIcon} />
                    }
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {r.name}
                        {r.clientName && r.clientName !== r.name ? ` (${r.clientName})` : ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        {r.type === 'vendor' ? 'Vendor' : 'Jobsite'} — {[r.address, r.city, r.state].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </Paper>
            )}
          </div>
        </ClickAwayListener>

        <Grid container spacing={2}>
          <Grid item xs={7}>
            <TextField
              label="Connection Type"
              value={form.connectionType}
              onChange={handleChange('connectionType')}
              fullWidth
              required
              select
              margin="dense"
              size="small"
            >
              {CONNECTION_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Connection Size"
              value={form.connectionSize}
              onChange={handleChange('connectionSize')}
              fullWidth
              select
              margin="dense"
              size="small"
              helperText="Optional"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {CONNECTION_SIZE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.label}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Typography className={classes.sectionLabel}>Address</Typography>
        <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
          <div className={classes.addressWrapper}>
            <TextField
              label="Search address..."
              value={form.addressInput}
              onChange={handleAddressInput}
              fullWidth
              margin="dense"
              size="small"
              placeholder="Start typing an address..."
              autoComplete="off"
              InputProps={{
                endAdornment: searching ? <CircularProgress size={16} style={{ marginRight: 4 }} /> : null
              }}
            />
            {showSuggestions && suggestions.length > 0 && (() => {
              const inputWords = form.addressInput.trim().toLowerCase().split(/\s+/)
              const visible = suggestions.filter(s => {
                const f = s.formatted?.toLowerCase() || ''
                return inputWords.every(w => f.includes(w))
              })
              if (visible.length === 0) return null
              return (
              <Paper className={classes.suggestionsDropdown} elevation={4}>
                {visible.map((s, idx) => (
                  <div
                    key={idx}
                    className={classes.suggestionItem}
                    onClick={() => handleSelectSuggestion(s)}
                  >
                    <LocationOnIcon className={classes.suggestionIcon} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{s.street}</div>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        {[s.city, s.state, s.zip].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </Paper>
              )
            })()}
          </div>
        </ClickAwayListener>

        {addressResolved && form.latitude && (
          <Typography className={classes.coordsResolved}>
            Coordinates resolved: {form.latitude}, {form.longitude}
          </Typography>
        )}

        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12}>
            <TextField
              label="Street"
              value={form.addressStreet}
              onChange={handleChange('addressStreet')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="City"
              value={form.addressCity}
              onChange={handleChange('addressCity')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="State"
              value={form.addressState}
              onChange={handleChange('addressState')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="ZIP"
              value={form.addressZip}
              onChange={handleChange('addressZip')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>

        <Typography className={classes.sectionLabel}>Coordinates</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Latitude"
              value={form.latitude}
              onChange={handleChange('latitude')}
              fullWidth
              required
              margin="dense"
              size="small"
              type="number"
              inputProps={{ step: 'any' }}
              placeholder="33.4484"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Longitude"
              value={form.longitude}
              onChange={handleChange('longitude')}
              fullWidth
              required
              margin="dense"
              size="small"
              type="number"
              inputProps={{ step: 'any' }}
              placeholder="-112.0740"
            />
          </Grid>
        </Grid>

        <Typography className={classes.sectionLabel}>Contact Info</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Contact Name"
              value={form.contactName}
              onChange={handleChange('contactName')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Phone"
              value={form.contactPhone}
              onChange={handleChange('contactPhone')}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>
        <TextField
          label="Email"
          value={form.contactEmail}
          onChange={handleChange('contactEmail')}
          fullWidth
          margin="dense"
          size="small"
        />
        <TextField
          label="Contact Notes"
          value={form.contactNotes}
          onChange={handleChange('contactNotes')}
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={2}
          placeholder="e.g. Ask for Dan at the front gate"
        />

        <Typography className={classes.sectionLabel}>Cost</Typography>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <TextField
              label="Cost Type"
              value={form.costType}
              onChange={handleChange('costType')}
              fullWidth
              select
              margin="dense"
              size="small"
            >
              {COST_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={7}>
            <TextField
              label="Cost Details"
              value={form.costNotes}
              onChange={handleChange('costNotes')}
              fullWidth
              margin="dense"
              size="small"
              placeholder="e.g. $15/fill"
            />
          </Grid>
        </Grid>

        <Typography className={classes.sectionLabel}>Fill Time</Typography>
        <TextField
          label="Estimated Fill Time (minutes)"
          value={form.estimatedFillMinutes}
          onChange={handleChange('estimatedFillMinutes')}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ min: 0 }}
          placeholder="Leave blank to use default based on connection type"
          helperText="Optional — overrides the default estimate for Near Me sorting"
        />

        <Typography className={classes.sectionLabel}>Availability</Typography>
        <TextField
          label="Availability Scope"
          value={form.availabilityScope}
          onChange={handleChange('availabilityScope')}
          fullWidth
          select
          margin="dense"
          size="small"
        >
          {AVAILABILITY_SCOPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        <Typography className={classes.sectionLabel}>Notes</Typography>
        <TextField
          value={form.notes}
          onChange={handleChange('notes')}
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={3}
          placeholder="General notes about this location..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || saving}
          style={{ backgroundColor: isValid && !saving ? '#2d5a3d' : undefined, color: isValid && !saving ? '#fff' : undefined }}
        >
          {saving ? 'Saving...' : (editItem ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WaterAccessFormDialog
